// src/lib/widgets.js
// Authoritative widget registry for Mudo Ceramics studio
// Used by: Manage Dashboard Widgets modal, Help Center Widgets tab
// This ensures Help Center always stays in sync with actual widgets

export const WIDGET_REGISTRY = [
  {
    id: "cost-planner",
    name: "Cost Planner",
    description: "Calculate and track production costs for ceramic pieces",
    category: "planning",
    requiresSubscription: false,
    howItWorks: [
      "Enter material costs (clay, glazes, additives)",
      "Set labor hours and hourly rate",
      "Assign costs to specific pieces or batches",
      "View total cost breakdown by category",
      "Track costs over time with historical data",
    ],
    tips: [
      "Update material costs regularly to keep calculations accurate",
      "Use batch mode for similar pieces to save time",
      "Export cost reports for accounting and pricing decisions",
      "Compare costs across different production methods",
    ],
  },
  {
    id: "mudo-map",
    name: "Mudo Map",
    description: "Interactive map for discovering studios and workshops",
    category: "discovery",
    requiresSubscription: false,
    howItWorks: [
      "View your studio location on the map",
      "Discover nearby studios and makers",
      "See workshop and class offerings",
      "Check real-time availability",
      "Book workshops directly from the map",
    ],
    tips: [
      "Update your studio profile to appear on the map",
      "Set accurate opening hours for visibility",
      "Use the map to find complementary businesses nearby",
      "Share your studio location link with customers",
    ],
  },
  {
    id: "streak",
    name: "Streak",
    description: "Track your daily ceramic work consistency",
    category: "motivation",
    requiresSubscription: false,
    howItWorks: [
      "Mark days when you work on ceramics",
      "Build a visual streak of consecutive days",
      "View your weekly and monthly productivity",
      "Get reminders to maintain your streak",
      "Share your streak with the community",
    ],
    tips: [
      "Even short work sessions count toward your streak",
      "Use streaks to build consistent creative habits",
      "Set realistic daily goals that you can maintain",
      "Take planned breaks when needed - streaks reset but you stay motivated",
    ],
  },
  {
    id: "journal",
    name: "Journal",
    description: "Record notes, ideas, and reflections on your ceramic practice",
    category: "reflection",
    requiresSubscription: false,
    howItWorks: [
      "Write journal entries about your work",
      "Attach photos of your creations",
      "Tag entries by theme, glaze, or technique",
      "Search and filter past entries",
      "Review your progress over time",
    ],
    tips: [
      "Take photos at each stage of your work process",
      "Record notes about what worked and what didn't",
      "Use tags to organize entries by technique or style",
      "Review old entries for inspiration and lessons learned",
    ],
  },
  {
    id: "daily-todo",
    name: "Daily To-Do",
    description: "Manage daily tasks and production schedule",
    category: "planning",
    requiresSubscription: false,
    howItWorks: [
      "Create and prioritize daily tasks",
      "Assign tasks to team members",
      "Track completion status",
      "Set due times and reminders",
      "Review completed tasks and daily summary",
    ],
    tips: [
      "Plan the night before for better productivity",
      "Break large projects into smaller daily tasks",
      "Prioritize high-value activities",
      "Review daily summaries to identify patterns",
    ],
  },
  {
    id: "tasks",
    name: "Tasks",
    description: "Organize and track projects and long-term work",
    category: "planning",
    requiresSubscription: false,
    howItWorks: [
      "Create tasks with due dates and priorities",
      "Add subtasks to break down complex projects",
      "Set dependencies and milestone targets",
      "Collaborate with team members on tasks",
      "Generate task reports for planning",
    ],
    tips: [
      "Use the kanban board to visualize workflow",
      "Set realistic deadlines based on past experience",
      "Review completed tasks to improve estimations",
      "Archive old tasks to keep your list clean",
    ],
  },
  {
    id: "analytics",
    name: "Analytics",
    description: "Track sales, inventory, and business metrics",
    category: "analytics",
    requiresSubscription: true,
    howItWorks: [
      "View sales trends by product and time period",
      "Track inventory levels and turnover",
      "Analyze customer demographics and preferences",
      "Monitor website and social media traffic",
      "Export reports for business planning",
    ],
    tips: [
      "Review analytics monthly to identify trends",
      "Compare metrics between seasons to plan production",
      "Track which products have highest profit margins",
      "Use customer data to inform marketing decisions",
    ],
  },
  {
    id: "materials",
    name: "Materials",
    description: "Manage clay, glazes, and other materials inventory",
    category: "inventory",
    requiresSubscription: false,
    howItWorks: [
      "Create an inventory of materials",
      "Track quantities and storage locations",
      "Log material usage for costing",
      "Set reorder points for low inventory",
      "View material usage by project or product",
    ],
    tips: [
      "Organize inventory by material type and supplier",
      "Set minimum inventory levels to avoid shortages",
      "Track expiration dates for time-sensitive materials",
      "Use usage data to optimize purchasing",
    ],
  },
  {
    id: "staff",
    name: "Staff",
    description: "Manage team members and schedules",
    category: "team",
    requiresSubscription: false,
    howItWorks: [
      "Add team members and assign roles",
      "Create and manage work schedules",
      "Track hours and time off",
      "Manage contact information and certifications",
      "Assign specific tasks to staff",
    ],
    tips: [
      "Keep staff contact information up to date",
      "Schedule staff based on production needs",
      "Track training and skill development",
      "Use schedule templates to save time",
    ],
  },
  {
    id: "utilities",
    name: "Utilities",
    description: "Monitor kiln and equipment utility usage",
    category: "operations",
    requiresSubscription: false,
    howItWorks: [
      "Track electricity and gas usage",
      "Log water consumption",
      "Monitor equipment maintenance schedules",
      "View utility costs over time",
      "Export utility reports",
    ],
    tips: [
      "Monitor kiln efficiency regularly",
      "Schedule maintenance to prevent downtime",
      "Track utility costs to price products accurately",
      "Look for opportunities to reduce energy consumption",
    ],
  },
  {
    id: "packaging",
    name: "Packaging",
    description: "Manage packaging materials and shipping",
    category: "sales",
    requiresSubscription: false,
    howItWorks: [
      "Create packaging templates for products",
      "Track packaging material inventory",
      "Log shipping costs and carriers",
      "Generate packing slips and labels",
      "Track packaging costs for costing analysis",
    ],
    tips: [
      "Use branded packaging to enhance customer experience",
      "Compare shipping carrier rates regularly",
      "Track packaging waste and costs",
      "Create templates for frequently shipped products",
    ],
  },
  {
    id: "marketing",
    name: "Marketing",
    description: "Plan and track marketing campaigns",
    category: "sales",
    requiresSubscription: false,
    howItWorks: [
      "Plan marketing campaigns and promotions",
      "Track marketing expense and ROI",
      "Monitor social media and email campaigns",
      "Manage email subscriber list",
      "Analyze customer acquisition sources",
    ],
    tips: [
      "Test different marketing messages and channels",
      "Track which campaigns generate the most sales",
      "Plan seasonal promotions ahead of time",
      "Build email list with valuable content",
    ],
  },
  {
    id: "reorder-po",
    name: "Reorder & POs",
    description: "Manage supplier orders and purchase orders",
    category: "inventory",
    requiresSubscription: false,
    howItWorks: [
      "Create purchase orders for suppliers",
      "Track order status and delivery dates",
      "Manage supplier information and pricing",
      "Receive and verify shipments",
      "Track spending by supplier",
    ],
    tips: [
      "Build relationships with key suppliers",
      "Compare prices across suppliers regularly",
      "Set reorder points to automate ordering",
      "Negotiate volume discounts",
    ],
  },
  {
    id: "workshops",
    name: "Workshops",
    description: "Host and manage pottery workshops and classes",
    category: "teaching",
    requiresSubscription: false,
    howItWorks: [
      "Create workshop offerings and schedules",
      "Manage student registrations and payments",
      "Track workshop capacity and attendance",
      "Send reminders to registered students",
      "Collect feedback from workshop attendees",
    ],
    tips: [
      "Offer workshops at consistent times for easier planning",
      "Set pricing based on materials and instructor time",
      "Use waiting lists when workshops fill up",
      "Request feedback to improve workshop quality",
    ],
  },
  {
    id: "templates",
    name: "Templates",
    description: "Create and manage production design templates",
    category: "design",
    requiresSubscription: false,
    howItWorks: [
      "Design and save pottery templates",
      "Organize templates by style or size",
      "Add notes about techniques and variations",
      "Share templates with team members",
      "Track which templates are most popular",
    ],
    tips: [
      "Document your most successful designs",
      "Create variations on popular templates",
      "Use templates to maintain consistency",
      "Update templates based on customer feedback",
    ],
  },
  {
    id: "studio-booking",
    name: "Studio Booking",
    description: "Manage studio space rentals and bookings",
    category: "facilities",
    requiresSubscription: false,
    howItWorks: [
      "List available studio space for rent",
      "Manage booking calendar and availability",
      "Process studio space bookings and payments",
      "Send booking confirmations to renters",
      "Track studio usage and revenue",
    ],
    tips: [
      "Price studio space competitively in your area",
      "Require deposits for bookings",
      "Maintain studio cleanliness standards",
      "Get reviews from studio renters",
    ],
  },
  {
    id: "staff-costs",
    name: "Staff Costs",
    description: "Track payroll and staff expenses",
    category: "finance",
    requiresSubscription: true,
    howItWorks: [
      "Record staff hours and calculate payroll",
      "Track benefits and deductions",
      "Generate payroll reports",
      "Calculate labor costs per project",
      "Manage tax withholdings",
    ],
    tips: [
      "Track hours regularly to avoid errors at payroll time",
      "Allocate labor costs to products for accurate pricing",
      "Keep detailed records for tax purposes",
      "Review labor cost percentages regularly",
    ],
  },
  {
    id: "receipts",
    name: "Receipts",
    description: "Record and organize business receipts and expenses",
    category: "finance",
    requiresSubscription: false,
    howItWorks: [
      "Capture and organize expense receipts",
      "Categorize expenses for tax purposes",
      "Generate expense reports",
      "Track spending by category and time period",
      "Prepare documents for tax filing",
    ],
    tips: [
      "Scan receipts immediately for organization",
      "Categorize expenses consistently",
      "Keep receipts for at least 7 years",
      "Review expense categories quarterly",
    ],
  },
  {
    id: "production",
    name: "Production",
    description: "Track ceramic production batches and units",
    category: "operations",
    requiresSubscription: false,
    howItWorks: [
      "Create production batches and track progress",
      "Log units produced at each stage",
      "Track kiln firings and results",
      "Monitor yield and waste",
      "Generate production reports",
    ],
    tips: [
      "Batch similar pieces for efficiency",
      "Document kiln firing results and temperatures",
      "Track yield rates to identify improvements",
      "Plan production based on sales forecast",
    ],
  },
  {
    id: "participants",
    name: "Participants",
    description: "Manage workshop participants and attendees",
    category: "teaching",
    requiresSubscription: false,
    howItWorks: [
      "Track workshop registration and attendance",
      "Manage participant information and preferences",
      "Send communications to participants",
      "Track participant progress and skills",
      "Generate attendance reports",
    ],
    tips: [
      "Build relationships with regular participants",
      "Track skill levels for appropriate class placement",
      "Send pre-workshop preparation information",
      "Follow up after workshops for feedback",
    ],
  },
  {
    id: "markets-checklist",
    name: "Markets Checklist",
    description: "Prepare for craft markets and pop-up sales",
    category: "sales",
    requiresSubscription: false,
    howItWorks: [
      "Create checklists for market prep",
      "Track inventory for specific markets",
      "Manage market booth setup and breakdown",
      "Calculate sales and costs per market",
      "Plan inventory needs for upcoming markets",
    ],
    tips: [
      "Start preparation 4-6 weeks before market",
      "Create a reusable checklist template",
      "Photograph display setups for consistency",
      "Track which products sell best at each market",
    ],
  },
  {
    id: "kiln-tracker",
    name: "Kiln Tracker",
    description: "Monitor and log kiln firings and maintenance",
    category: "operations",
    requiresSubscription: false,
    howItWorks: [
      "Log each kiln firing with temperature and duration",
      "Track kiln maintenance and repairs",
      "Monitor kiln performance and efficiency",
      "Plan kiln cycles for production schedule",
      "Generate kiln usage reports",
    ],
    tips: [
      "Keep detailed firing logs for quality control",
      "Schedule regular kiln maintenance",
      "Document any kiln modifications or upgrades",
      "Monitor energy consumption per firing",
    ],
  },
];

/**
 * Get widget by ID
 */
export function getWidget(widgetId) {
  return WIDGET_REGISTRY.find((w) => w.id === widgetId);
}

/**
 * Get all widgets for a category
 */
export function getWidgetsByCategory(category) {
  return WIDGET_REGISTRY.filter((w) => w.category === category);
}

/**
 * Get all categories
 */
export function getWidgetCategories() {
  const categories = new Set(WIDGET_REGISTRY.map((w) => w.category));
  return Array.from(categories).sort();
}

/**
 * Get all widgets that require subscription
 */
export function getSubscriptionRequiredWidgets() {
  return WIDGET_REGISTRY.filter((w) => w.requiresSubscription);
}
